import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Loader2, ArrowRight, CheckCircle2, PawPrint, MapPin, Calendar, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { DogRegistrationStep } from '../components/DogRegistrationStep';
import { toast } from 'sonner';

const ACTIVITIES = [
  {
    id: 'social_walks',
    icon: PawPrint,
    title: 'Social Walks & Meetups',
    desc: 'Hear about upcoming dog walks, social meetups and opportunities to meet other local dog owners.',
  },
  {
    id: 'onsite_events',
    icon: Calendar,
    title: 'Events & Activities',
    desc: 'Get updates about dog-friendly events, seasonal activities, special days and other things happening at Ultimate Pet Foods.',
  },
  {
    id: 'dog_training',
    icon: GraduationCap,
    title: 'Training & Learning',
    desc: 'Hear about dog training sessions, workshops, demonstrations and other opportunities to learn with your dog.',
  },
];

function formatError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export const FFOSSignUpPage = () => {
  useDocumentTitle(
    "Ted's Passport — Join Dog Activities at Ffos Caerffili",
    "Sign up to Ted's Passport and join our social walks, onsite events and dog training sessions at Ffos Caerffili."
  );
  const navigate = useNavigate();
  const { register, customer } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signedUp, setSignedUp] = useState(false);
  const [dogsStep, setDogsStep] = useState('prompt'); // 'prompt' | 'form' | 'done'

  // If we were sent back here from the Google OAuth callback (?welcome=1),
  // show the thank-you panel instead of the signup form or an auto-redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('welcome') === '1') {
      setSignedUp(true);
    }
  }, []);

  // Only redirect already-logged-in visitors who are NOT coming from a fresh
  // signup. Fresh signups stay here to see the thank-you panel.
  useEffect(() => {
    if (customer && !signedUp) navigate('/');
  }, [customer, signedUp, navigate]);

  const toggleActivity = (id) => {
    setSelectedActivities(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const passwordStrength = (() => {
    if (!password) return null;
    const long = password.length >= 8;
    const varied = /[A-Za-z]/.test(password) && /[0-9]/.test(password);
    if (long && varied) return { label: 'Strong', color: 'text-emerald-600', bar: 'bg-emerald-500 w-full' };
    if (long || varied) return { label: 'OK', color: 'text-amber-600', bar: 'bg-amber-500 w-2/3' };
    return { label: 'Weak', color: 'text-red-500', bar: 'bg-red-500 w-1/3' };
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Please enter your name'); return; }
    if (!email.trim()) { setError('Please enter your email'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (selectedActivities.length === 0) {
      setError('Please pick at least one activity to join');
      return;
    }

    setSubmitting(true);
    try {
      await register(name.trim(), email.trim(), password, 'ffos', selectedActivities);
      toast.success(`Welcome to Ted's Passport, ${name.trim().split(' ')[0]}!`);
      setSignedUp(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignup = () => {
    setError('');
    if (selectedActivities.length === 0) {
      setError('Please select at least one activity before continuing with Google');
      // Scroll the activity section into view so the user sees the hint
      const el = document.querySelector('[data-testid="ffos-activities-group"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // Stash the selections so the Emergent-auth round-trip can honour them
    sessionStorage.setItem(
      'pending_ffos_signup',
      JSON.stringify({ source: 'ffos', tags: selectedActivities })
    );
    const redirectUrl = window.location.origin + '/ffos';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Thank-you panel — shown after a successful email signup, or when the
  // Google OAuth callback lands back here with ?welcome=1.
  if (signedUp) {
    const firstName = (customer?.name || name || '').split(' ')[0];
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          backgroundColor: '#1f6d62',
          backgroundImage: `
            radial-gradient(circle at 15% 20%, rgba(160, 218, 57, 0.10) 0%, transparent 45%),
            radial-gradient(circle at 85% 85%, rgba(160, 218, 57, 0.12) 0%, transparent 50%)
          `,
        }}
        data-testid="ffos-thanks-page"
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 sm:p-10 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 rounded-full bg-[#a0da39]/15 border-4 border-[#a0da39] flex items-center justify-center mx-auto mb-5"
          >
            <CheckCircle2 className="w-10 h-10 text-[#1f6d62]" strokeWidth={2.5} />
          </motion.div>

          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-white bg-[#1f6d62] mb-3">
            <PawPrint className="w-3 h-3" /> Ted&apos;s Passport
          </span>
          <h1 className="text-3xl sm:text-4xl font-black text-[#1a1a1a] tracking-tight mb-2">
            {firstName ? `Thanks, ${firstName}!` : 'Thanks for signing up!'}
          </h1>
          <p
            className="text-[#0FA49C] text-xl sm:text-2xl italic mb-4"
            style={{ fontFamily: "'Caveat', 'Dancing Script', cursive" }}
          >
            You&apos;re on the list.
          </p>

          {dogsStep === 'prompt' && (
            <>
              <p className="text-gray-600 text-sm leading-relaxed mb-6 max-w-sm mx-auto">
                Your Ted&apos;s Passport account is ready. Want to add your dog(s) so Ted can personalise recommendations and remember their birthdays?
              </p>

              <div className="bg-[#f4f7ea] rounded-xl p-4 mb-6 text-left">
                <p className="text-[11px] font-bold uppercase tracking-widest text-[#1f6d62] mb-2">What happens next</p>
                <ul className="space-y-1.5 text-xs text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#0FA49C] flex-shrink-0 mt-0.5" />
                    <span>You can manage your preferences any time from your account.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#0FA49C] flex-shrink-0 mt-0.5" />
                    <span>No spam — we&apos;ll only email when something relevant is on.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#0FA49C] flex-shrink-0 mt-0.5" />
                    <span>Unsubscribe any time, no questions asked.</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-2">
                <Button
                  onClick={() => setDogsStep('form')}
                  className="w-full h-12 bg-[#1f6d62] hover:bg-[#174f46] text-white font-extrabold uppercase tracking-wide rounded-full shadow-lg transition-all"
                  data-testid="ffos-add-dog-btn"
                >
                  <PawPrint className="w-4 h-4 mr-2" /> Add my dog
                </Button>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="w-full py-2.5 text-sm font-bold text-gray-500 hover:text-[#1f6d62] transition-colors"
                  data-testid="ffos-thanks-home-btn"
                >
                  Skip & return to home
                </button>
              </div>
            </>
          )}

          {dogsStep === 'form' && (
            <div className="text-left mt-2">
              <DogRegistrationStep
                maxDogs={2}
                onDone={() => setDogsStep('done')}
                onSkip={() => setDogsStep('done')}
                skipLabel="I'll do this later"
              />
            </div>
          )}

          {dogsStep === 'done' && (
            <>
              <p className="text-gray-600 text-sm leading-relaxed mb-6 max-w-sm mx-auto">
                You&apos;re all set. We&apos;ll be in touch when the next walk, event or training session matching your picks is coming up at Ffos Caerffili.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="w-full h-12 bg-[#1f6d62] hover:bg-[#174f46] text-white font-extrabold uppercase tracking-wide rounded-full shadow-lg transition-all"
                data-testid="ffos-thanks-home-btn"
              >
                Return to Home <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: '#1f6d62',
        backgroundImage: `
          radial-gradient(circle at 15% 20%, rgba(160, 218, 57, 0.10) 0%, transparent 45%),
          radial-gradient(circle at 85% 85%, rgba(160, 218, 57, 0.12) 0%, transparent 50%)
        `,
      }}
      data-testid="ffos-signup-page"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          {/* Left — intro */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 text-white order-2 lg:order-1"
          >
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-[#1f6d62] bg-[#a0da39] mb-3">
              <PawPrint className="w-3 h-3" /> Ted&apos;s Passport
            </span>
            <h1 className="font-extrabold uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl">
              Join Dog Activities at
              <span className="text-[#a0da39] block">Ffos Caerffili</span>
            </h1>
            <p
              className="text-[#a0da39] mt-3 text-xl sm:text-2xl italic"
              style={{ fontFamily: "'Caveat', 'Dancing Script', cursive" }}
            >
              Walks, events, training — all in one passport.
            </p>
            <p className="text-white/85 mt-4 text-sm leading-relaxed max-w-md">
              Create a free Ted&apos;s Passport account and pick which activities you&apos;d like to hear about. We&apos;ll keep you in the loop whenever the next walk, event or training session is scheduled.
            </p>

            <div className="mt-6 pt-6 border-t border-white/15 space-y-3 text-sm text-white/85">
              <div className="flex items-center gap-2.5">
                <MapPin className="w-4 h-4 text-[#a0da39] flex-shrink-0" />
                <span>Based at Ffos Caerffili, South Wales</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-[#a0da39] flex-shrink-0" />
                <span>Free to join — no commitment</span>
              </div>
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-[#a0da39] flex-shrink-0" />
                <span>Unsubscribe any time from your account</span>
              </div>
            </div>
          </motion.div>

          {/* Right — form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-3 order-1 lg:order-2"
          >
            <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8">
              <h2 className="text-2xl font-black text-[#1a1a1a] mb-1">Create your passport</h2>
              <p className="text-sm text-gray-600 mb-6">Pick the activities you&apos;d love to join and we&apos;ll keep you posted.</p>

              {error && (
                <div
                  className="mb-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded-xl p-3"
                  data-testid="ffos-signup-error"
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="ffos-signup-form">
                {/* Name */}
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Your name</label>
                  <div className="relative mt-1.5">
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Jane Smith"
                      className="pl-10 h-11"
                      disabled={submitting}
                      autoComplete="name"
                      data-testid="ffos-signup-name"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Email</label>
                  <div className="relative mt-1.5">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10 h-11"
                      disabled={submitting}
                      autoComplete="email"
                      data-testid="ffos-signup-email"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Password</label>
                  <div className="relative mt-1.5">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className="pl-10 pr-16 h-11"
                      disabled={submitting}
                      autoComplete="new-password"
                      data-testid="ffos-signup-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold uppercase text-gray-500 hover:text-[#0FA49C]"
                      tabIndex={-1}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {passwordStrength && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="h-1.5 flex-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${passwordStrength.bar}`} />
                      </div>
                      <span className={`text-xs font-bold ${passwordStrength.color}`}>{passwordStrength.label}</span>
                    </div>
                  )}
                </div>

                {/* Activity selection */}
                <div className="pt-2" data-testid="ffos-activities-group">
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                    Select the activities you&apos;d like to hear about <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 mb-3">Choose the activities you&apos;re interested in and we&apos;ll email you when something relevant is coming up. You can update your preferences at any time.</p>
                  <div className="space-y-2.5">
                    {ACTIVITIES.map((a) => {
                      const checked = selectedActivities.includes(a.id);
                      const Icon = a.icon;
                      return (
                        <label
                          key={a.id}
                          className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                            checked
                              ? 'border-[#0FA49C] bg-[#0FA49C]/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid={`ffos-activity-${a.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleActivity(a.id)}
                            className="mt-1 w-4 h-4 accent-[#0FA49C]"
                            disabled={submitting}
                            data-testid={`ffos-activity-${a.id}-checkbox`}
                          />
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${checked ? 'bg-[#0FA49C]' : 'bg-gray-100'}`}>
                            <Icon className={`w-4 h-4 ${checked ? 'text-white' : 'text-gray-500'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-[#1a1a1a] text-sm">{a.title}</p>
                            <p className="text-xs text-gray-600 leading-relaxed">{a.desc}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-[#1f6d62] hover:bg-[#174f46] text-white font-extrabold uppercase tracking-wide rounded-full shadow-lg transition-all"
                  data-testid="ffos-signup-submit"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating passport…</>
                  ) : (
                    <>Create Ted&apos;s Passport <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>

                {/* Divider */}
                <div className="relative py-1">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-3 text-[11px] font-bold uppercase tracking-widest text-gray-400">or</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleSignup}
                  disabled={submitting}
                  className="w-full h-12 bg-white hover:bg-gray-50 text-[#1a1a1a] font-bold rounded-full border-2 border-gray-200 hover:border-gray-300 shadow-sm transition-all flex items-center justify-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="ffos-google-signup"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>
                <p className="text-[11px] text-center text-gray-400 -mt-1">Tick at least one activity above before using Google</p>

                <p className="text-xs text-gray-500 text-center">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="text-[#0FA49C] font-bold hover:underline"
                  >
                    Sign in from the home page
                  </button>
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
